class UpsellDiscount {
  static CACHE_KEY = 'upsellDiscountCache';
  static CART_ID_KEY = 'upsellPreviewCartId';
  static THROTTLE_KEY = 'upsellThrottled';
  static CACHE_TTL = 30 * 60 * 1000; // 30 minutes

  // Reusable GraphQL fragment for cart lines with cost
  static CART_LINES_FRAGMENT = `
    lines(first: 100) {
      nodes {
        quantity
        attributes { key value }
        cost {
          subtotalAmount { amount }
          totalAmount { amount }
        }
      }
    }
  `;

  constructor(root) {
    this.root = root;
    this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    this.moneyWithCurrencyFormat = root.dataset.moneyWithCurrencyFormat || '${{amount}}';
    this.currencyCodeEnabled = root.dataset.currencyCodeEnabled === 'true';
    this.apiVersion = root.dataset.storefrontApiVersion || '2024-07';
  }

  init() {
    const priceBlocks = Array.from(this.root.querySelectorAll('[data-upsell-price]'));
    if (priceBlocks.length === 0) return;

    this.fetchCartLines()
      .then((baseLines) => baseLines && this.updatePriceBlocks(priceBlocks, baseLines))
      .catch((err) => console.warn('[UpsellDiscount] Failed to update prices', err));
  }

  // GraphQL helper with throttle detection
  gql(query, variables = {}) {
    return fetch(`/api/${this.apiVersion}/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables }),
    })
      .then((res) => res.json())
      .then((payload) => {
        if (payload?.errors?.some((e) => e.extensions?.code === 'THROTTLED')) {
          const err = new Error('Throttled');
          err.isThrottled = true;
          throw err;
        }
        if (payload?.errors?.length) {
          throw new Error(payload.errors.map((e) => e.message).join(', '));
        }
        return payload?.data;
      });
  }

  // Helper to check userErrors from mutations
  checkUserErrors(userErrors) {
    if (userErrors?.length > 0) {
      throw new Error(userErrors.map((e) => e.message).join(', '));
    }
  }

  fetchCartLines() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((cart) => {
        if (!cart?.items) return null;
        return cart.items.map((item) => ({
          merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
          quantity: item.quantity,
        }));
      });
  }

  updatePriceBlocks(priceBlocks, baseLines) {
    const blocksWithVariant = priceBlocks
      .map((block) => ({ block, variantId: block.dataset.variantId }))
      .filter((item) => item.variantId);

    if (blocksWithVariant.length === 0) return;

    const variantIds = blocksWithVariant.map((item) => item.variantId);
    const cacheKey = this.getCacheKey(baseLines, variantIds);
    const cached = this.getFromCache(cacheKey);

    if (cached) {
      this.applyDiscounts(blocksWithVariant, cached);
      return;
    }

    blocksWithVariant.forEach(({ block }) => this.setLoading(block, true));

    const previewLines = blocksWithVariant.map(({ variantId }) => ({
      merchandiseId: `gid://shopify/ProductVariant/${variantId}`,
      quantity: 1,
      attributes: [{ key: 'upsell_preview', value: variantId }],
    }));

    return this.getDiscountedPrices([...baseLines, ...previewLines])
      .then((linesData) => {
        if (linesData) {
          this.saveToCache(cacheKey, linesData);
          this.applyDiscounts(blocksWithVariant, linesData);
        }
      })
      .finally(() => {
        blocksWithVariant.forEach(({ block }) => this.setLoading(block, false));
      });
  }

  // Smart hybrid: parallel execution when throttled
  getDiscountedPrices(lines) {
    if (!this.isThrottledMode()) {
      return this.cartCreateWithLines(lines).catch((err) => {
        if (err.isThrottled) {
          this.setThrottledMode(true);
          return this.fallbackPersistentCart(lines);
        }
        throw err;
      });
    }

    // Throttled mode: run fallback immediately, cartCreate as background check
    const fallbackPromise = this.fallbackPersistentCart(lines);

    // Background check if throttle lifted (don't await, just check)
    this.cartCreateWithLines(lines)
      .then(() => this.setThrottledMode(false))
      .catch(() => {}); // Ignore errors

    return fallbackPromise;
  }

  isThrottledMode() {
    try {
      return localStorage.getItem(UpsellDiscount.THROTTLE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  setThrottledMode(throttled) {
    try {
      if (throttled) {
        localStorage.setItem(UpsellDiscount.THROTTLE_KEY, 'true');
      } else {
        localStorage.removeItem(UpsellDiscount.THROTTLE_KEY);
      }
    } catch { /* ignore */ }
  }

  cartCreateWithLines(lines) {
    return this.gql(
      `mutation ($lines: [CartLineInput!]!) {
        cartCreate(input: { lines: $lines }) {
          cart { ${UpsellDiscount.CART_LINES_FRAGMENT} }
          userErrors { field message }
        }
      }`,
      { lines }
    ).then((data) => {
      this.checkUserErrors(data?.cartCreate?.userErrors);
      return data?.cartCreate?.cart?.lines?.nodes || [];
    });
  }

  fallbackPersistentCart(lines) {
    let storedCartId = null;
    try {
      storedCartId = localStorage.getItem(UpsellDiscount.CART_ID_KEY);
    } catch {
      storedCartId = null;
    }

    const execute = (cartId) =>
      this.clearCart(cartId).then(() => this.addLinesToCart(cartId, lines));

    if (storedCartId) {
      return execute(storedCartId).catch((err) => {
        if (this.isCartError(err)) {
          localStorage.removeItem(UpsellDiscount.CART_ID_KEY);
          return this.createEmptyCart().then(execute);
        }
        throw err;
      });
    }

    return this.createEmptyCart().then(execute);
  }

  createEmptyCart() {
    return this.gql(`
      mutation {
        cartCreate(input: {}) {
          cart { id }
          userErrors { field message }
        }
      }
    `).then((data) => {
      this.checkUserErrors(data?.cartCreate?.userErrors);
      const cartId = data?.cartCreate?.cart?.id;
      if (cartId) localStorage.setItem(UpsellDiscount.CART_ID_KEY, cartId);
      return cartId;
    });
  }

  clearCart(cartId) {
    return this.gql(
      `query ($cartId: ID!) {
        cart(id: $cartId) {
          lines(first: 100) { nodes { id } }
        }
      }`,
      { cartId }
    ).then((data) => {
      const lineIds = data?.cart?.lines?.nodes?.map((n) => n.id) || [];
      if (lineIds.length === 0) return null;

      return this.gql(
        `mutation ($cartId: ID!, $lineIds: [ID!]!) {
          cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
            userErrors { field message }
          }
        }`,
        { cartId, lineIds }
      ).then((removeData) => {
        this.checkUserErrors(removeData?.cartLinesRemove?.userErrors);
      });
    });
  }

  addLinesToCart(cartId, lines) {
    return this.gql(
      `mutation ($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart { ${UpsellDiscount.CART_LINES_FRAGMENT} }
          userErrors { field message }
        }
      }`,
      { cartId, lines }
    ).then((data) => {
      this.checkUserErrors(data?.cartLinesAdd?.userErrors);
      return data?.cartLinesAdd?.cart?.lines?.nodes || [];
    });
  }

  isCartError(error) {
    const msg = error?.message?.toLowerCase() || '';
    return msg.includes('cart') && (msg.includes('not found') || msg.includes('invalid'));
  }

  applyDiscounts(blocksWithVariant, linesData) {
    blocksWithVariant.forEach(({ block, variantId }) => {
      const line = linesData.find((l) =>
        l.attributes?.some((a) => a.key === 'upsell_preview' && a.value === variantId)
      );
      if (!line) return;

      const qty = line.quantity || 1;
      const subtotal = parseFloat(line.cost?.subtotalAmount?.amount);
      const total = parseFloat(line.cost?.totalAmount?.amount);

      if (!isNaN(subtotal) && !isNaN(total) && total < subtotal) {
        this.renderDiscounted(block, subtotal / qty, total / qty);
      }
    });
  }

  getCacheKey(baseLines, variantIds) {
    const cartKey = baseLines.map((l) => `${l.merchandiseId}:${l.quantity}`).sort().join(',');
    return `${cartKey}|${variantIds.sort().join(',')}`;
  }

  getFromCache(key) {
    try {
      const cache = JSON.parse(localStorage.getItem(UpsellDiscount.CACHE_KEY) || '{}');
      const entry = cache[key];
      if (entry && Date.now() - entry.ts < UpsellDiscount.CACHE_TTL) {
        return entry.data;
      }
    } catch { /* ignore */ }
    return null;
  }

  saveToCache(key, data) {
    try {
      const cache = JSON.parse(localStorage.getItem(UpsellDiscount.CACHE_KEY) || '{}');
      const now = Date.now();
      const cleaned = Object.fromEntries(
        Object.entries(cache).filter(([, v]) => now - v.ts < UpsellDiscount.CACHE_TTL)
      );
      cleaned[key] = { ts: now, data };
      localStorage.setItem(UpsellDiscount.CACHE_KEY, JSON.stringify(cleaned));
    } catch { /* ignore */ }
  }

  setLoading(block, isLoading) {
    const loader = block.querySelector('[data-upsell-price-loader]');
    block.classList.toggle('is-loading', isLoading);
    loader?.classList.toggle('hidden', !isLoading);
  }

  renderDiscounted(block, regularPrice, salePrice) {
    const currentEl = block.querySelector('[data-upsell-price-current]');
    const oldEl = block.querySelector('[data-upsell-price-compare]');
    if (!currentEl || !oldEl) return;

    currentEl.textContent = this.formatMoney(salePrice);
    oldEl.textContent = this.formatMoney(regularPrice);
    oldEl.classList.remove('hidden');

    // Update discount badge
    const item = block.closest('[data-upsell-item]');
    const badge = item?.querySelector('[data-upsell-discount-badge]');
    if (badge) {
      const isValidDiscount =
        Number.isFinite(regularPrice) && Number.isFinite(salePrice) && regularPrice > 0;
      if (!isValidDiscount) {
        badge.textContent = '0%';
        return;
      }
      const discountPercent = Math.round((1 - salePrice / regularPrice) * 100);
      badge.textContent = `-${discountPercent}%`;
    }
  }

  formatMoney(amount) {
    const cents = Math.round(amount * 100);
    if (isNaN(cents)) return '';
    const format = this.currencyCodeEnabled ? this.moneyWithCurrencyFormat : this.moneyFormat;
    return window.Shopify?.formatMoney?.(cents, format) || '';
  }
}

(() => {
  // Initialization logic
  const pendingUpsellRoots = new Set();

  const initUpsellDiscountRoot = (root) => {
    if (root.dataset.upsellDiscountInitialized === 'true') return;
    root.dataset.upsellDiscountInitialized = 'true';
    new UpsellDiscount(root).init();
  };

  const scheduleUpsellInit = (root) => {
    if (root.dataset.upsellDiscountInitialized === 'true') return;
    const drawer = root.closest('cart-drawer');
    if (!drawer || drawer.classList.contains('active')) {
      initUpsellDiscountRoot(root);
    } else {
      pendingUpsellRoots.add(root);
    }
  };

  const flushPendingUpsellRoots = (drawer) => {
    if (!drawer?.classList.contains('active')) return;
    pendingUpsellRoots.forEach((root) => {
      if (!document.contains(root)) {
        pendingUpsellRoots.delete(root);
      } else if (root.closest('cart-drawer') === drawer) {
        initUpsellDiscountRoot(root);
        pendingUpsellRoots.delete(root);
      }
    });
  };

  const observeCartDrawer = (drawer) => {
    if (!drawer || drawer.dataset.upsellDiscountObserved === 'true') return;
    drawer.dataset.upsellDiscountObserved = 'true';
    new MutationObserver(() => flushPendingUpsellRoots(drawer))
      .observe(drawer, { attributes: true, attributeFilter: ['class'] });
    flushPendingUpsellRoots(drawer);
  };

  // Initial setup
  document.querySelectorAll('[data-upsell-discount-root]').forEach(scheduleUpsellInit);
  observeCartDrawer(document.querySelector('cart-drawer'));

  // Watch for dynamically added elements
  new MutationObserver((mutations) => {
    for (const { addedNodes } of mutations) {
      for (const node of addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches('cart-drawer')) observeCartDrawer(node);
        if (node.matches('[data-upsell-discount-root]')) {
          scheduleUpsellInit(node);
        } else {
          node.querySelectorAll?.('[data-upsell-discount-root]').forEach(scheduleUpsellInit);
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
